/**
 * Ranking de precedência das fontes do horário efetivo — constantes e normalização.
 *
 * Mora num módulo `.ts`, e não dentro do SFC, porque `tsc --noemit` (o `check-types`
 * do repo) só enxerga `*.vue` pelo shim de export default: um named export vindo de
 * um `.vue` quebra o type-check de qualquer `.ts` que o importe.
 */
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'

/**
 * Fonte travada no fim do ranking — somente histórico, nunca escolhível (Q4/Q16).
 * Não entra no `modelValue`: o que a política guarda é só a ordem configurável.
 */
export const PINNED_SOURCE_KIND = EffectiveSourceKind.MANUAL_LEGACY

/**
 * Ordem de fábrica da Q16 (M1-1), a mesma do `DEFAULT_SOURCE_PRECEDENCE` do backend
 * (F1). `LEGACY` e `MANUAL_LEGACY` ficam de fora: não são configuráveis.
 */
export const DEFAULT_SOURCE_PRECEDENCE: EffectiveSourceKind[] = [
  EffectiveSourceKind.SHEET,
  EffectiveSourceKind.APP,
  EffectiveSourceKind.EXTERNAL_APP,
  EffectiveSourceKind.ACTIVITY_RECORD,
  EffectiveSourceKind.AUTO_PERMANENCE
]

/**
 * Nome da fonte NESTA tela. `ACTIVITY_RECORD` aparece como "Registro de Atividade
 * aprovado" aqui (e só aqui): o ranking é sobre o que já passou pela auditoria, e o
 * rótulo curto de `EFFECTIVE_SOURCE_KIND_LABELS` — usado nos cards, onde o estado
 * "aprovado 12/09" já aparece ao lado — deixaria a regra ambígua na política.
 */
export const SOURCE_PRECEDENCE_NAMES: Record<EffectiveSourceKind, string> = {
  [EffectiveSourceKind.SHEET]: 'Folha auditada',
  [EffectiveSourceKind.APP]: 'App Minha Escala',
  [EffectiveSourceKind.EXTERNAL_APP]: 'App Externo',
  [EffectiveSourceKind.ACTIVITY_RECORD]: 'Registro de Atividade aprovado',
  [EffectiveSourceKind.AUTO_PERMANENCE]: 'Permanência automática',
  [EffectiveSourceKind.MANUAL_LEGACY]: 'Manual (legado)',
  [EffectiveSourceKind.LEGACY]: 'Legado'
}

export const SOURCE_PRECEDENCE_DESCRIPTIONS: Record<EffectiveSourceKind, string> = {
  [EffectiveSourceKind.SHEET]: 'Linha de folha aprovada na auditoria',
  [EffectiveSourceKind.APP]: 'Check com geolocalização ou biometria',
  [EffectiveSourceKind.EXTERNAL_APP]: 'Doctor ID, Pega Plantão e afins',
  [EffectiveSourceKind.ACTIVITY_RECORD]: 'Equivalência declarada e aprovada pela auditoria',
  [EffectiveSourceKind.AUTO_PERMANENCE]: 'Check-out gerado pela regra de permanência da escala',
  [EffectiveSourceKind.MANUAL_LEGACY]: 'Travado ao final · somente histórico',
  [EffectiveSourceKind.LEGACY]: 'Somente histórico'
}

/**
 * Normaliza o que vier do backend: descarta fontes não configuráveis e repetidas e
 * completa a lista com as que faltarem, na ordem de fábrica. Sem isso, uma política
 * salva antes de uma fonte nova existir sumiria com ela da tela.
 */
export function sanitizeSourcePrecedence(
  value?: EffectiveSourceKind[] | null
): EffectiveSourceKind[] {
  const seen = new Set<EffectiveSourceKind>()
  const ordered = (value ?? []).filter((kind) => {
    if (!DEFAULT_SOURCE_PRECEDENCE.includes(kind) || seen.has(kind)) return false
    seen.add(kind)
    return true
  })
  return [...ordered, ...DEFAULT_SOURCE_PRECEDENCE.filter((kind) => !seen.has(kind))]
}
